/**
 * Edge Definition Types for Reactive Edge System
 *
 * E018-S009: Types for edge derivation and sync.
 * These are the "desired state" types, separate from rendering types.
 */

// =============================================================================
// Edge Definition Types
// =============================================================================

/**
 * Type of edge.
 */
export type EdgeType = 'automation' | 'relation';

/**
 * Metadata for automation edges.
 */
export interface AutomationEdgeMetadata {
  readonly kind: 'automation';
  /** Direction: input (DB→Auto) or output (Auto→DB) */
  readonly direction: 'input' | 'output';
  /** Automation block ID */
  readonly automationId: string;
  /** Database ID (the one connected to automation) */
  readonly databaseId: string;
}

/**
 * Metadata for relation edges.
 */
export interface RelationEdgeMetadata {
  readonly kind: 'relation';
  /** Source database ID */
  readonly sourceDatabaseId: string;
  /** Target database ID */
  readonly targetDatabaseId: string;
  /** Relation column ID that creates this edge */
  readonly relationColumnId: string;
  /** Source side cardinality */
  readonly sourceCardinality: 'one' | 'many';
  /** Target side cardinality */
  readonly targetCardinality: 'one' | 'many';
  /** Whether this is a bidirectional relation */
  readonly isBidirectional: boolean;
  /** Inverse column ID (if bidirectional) */
  readonly inverseColumnId?: string;
}

/**
 * Metadata for File Index source edges.
 */
export interface FileIndexEdgeMetadata {
  readonly kind: 'file-index';
  /** File Index database block ID */
  readonly sourceDatabaseId: string;
  /** Source image/attachment block ID */
  readonly sourceBlockId: string;
}

/**
 * Union type for edge metadata.
 */
export type EdgeMetadata =
  | AutomationEdgeMetadata
  | RelationEdgeMetadata
  | FileIndexEdgeMetadata;

/**
 * Definition of an edge that should exist.
 * Used by EdgeDerivationService (output) and EdgeSyncService (input).
 *
 * GUARANTEE: sourceId and targetId are element IDs that ARE in gfx.layer.
 */
export interface EdgeDefinition {
  /** Unique ID for reconciliation */
  readonly id: string;

  /** Source element ID - GUARANTEED in gfx.layer */
  readonly sourceId: string;

  /** Target element ID - GUARANTEED in gfx.layer */
  readonly targetId: string;

  /** Edge type */
  readonly type: EdgeType;

  /** Type-specific metadata */
  readonly metadata: EdgeMetadata;

  /** Anchor hint for source side */
  readonly sourceAnchor?: 'left' | 'right' | 'top' | 'bottom' | 'auto';

  /** Anchor hint for target side */
  readonly targetAnchor?: 'left' | 'right' | 'top' | 'bottom' | 'auto';
}

/**
 * Result of edge sync operation.
 */
export interface EdgeSyncResult {
  /** Edge IDs that were added */
  readonly added: string[];
  /** Edge IDs that were removed */
  readonly removed: string[];
  /** Number of edges that remained unchanged */
  readonly unchanged: number;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Type guard for automation edge metadata.
 */
export function isAutomationEdgeMetadata(
  metadata: EdgeMetadata
): metadata is AutomationEdgeMetadata {
  return metadata.kind === 'automation';
}

/**
 * Type guard for relation edge metadata.
 */
export function isRelationEdgeMetadata(
  metadata: EdgeMetadata
): metadata is RelationEdgeMetadata {
  return metadata.kind === 'relation';
}

/**
 * Type guard for File Index edge metadata.
 */
export function isFileIndexEdgeMetadata(
  metadata: EdgeMetadata
): metadata is FileIndexEdgeMetadata {
  return metadata.kind === 'file-index';
}

/**
 * Generates a unique edge ID from definition.
 */
export function generateEdgeDefinitionId(
  type: EdgeType,
  sourceId: string,
  targetId: string,
  discriminator?: string
): string {
  const base = `${type}-${sourceId}-${targetId}`;
  return discriminator ? `${base}-${discriminator}` : base;
}
